import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCheck, Repeat2, Star, Trophy } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import useResultEntrance, { resultMetricDelay, resultRecordDelay, resultRevealStyle } from '../hooks/useResultEntrance';
import './SessionSummary.css';
import './ResultEntrance.css';
import './WritingResultPage.css';

const w = (key, values = {}) => t(`writingExam.${key}`, undefined, values);
const clock = value => {
  if (value == null) return '—';
  const seconds = Math.floor(Math.max(0, value) / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

export default function WritingResultPage() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const [params] = useSearchParams();
  const batch = params.get('batch') || '1';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  const requestRef = useRef(null);
  const entrance = useResultEntrance(Boolean(data) && !loading, `${examId}:${batch}`);
  const load = useCallback(async () => {
    const request = {};
    requestRef.current = request;
    setLoading(true); setError(''); setData(null);
    try {
      const result = await apiClient.getWritingResult(examId, batch);
      if (request === requestRef.current) setData(result);
    } catch (err) {
      if (request === requestRef.current) setError(err.message || w('failed'));
    } finally { if (request === requestRef.current) setLoading(false); }
  }, [examId, batch]);
  useEffect(() => { void load(); window.scrollTo?.(0, 0); return () => { requestRef.current = null; }; }, [load]);
  useEffect(() => { if (!loading) headingRef.current?.focus(); }, [loading]);
  const metrics = data ? [
    ['resultCompleted', `${data.completed} / ${data.total}`, CheckCheck],
    ['resultAverage', data.averageScore == null ? '—' : w('score', { score: data.averageScore }), Star],
    ['resultRepeats', data.repeats, Repeat2],
    ['resultPoints', data.scoreGained, Trophy],
  ] : [];
  return <main className="writing-result-page">
    <section className={`session-summary ${entrance.className}`} onAnimationStart={entrance.onAnimationStart} aria-labelledby="writing-result-title">
      <header className="session-summary-header result-reveal">
        <h1 id="writing-result-title" ref={headingRef} tabIndex={-1}>{w('resultTitle')}</h1>
        {data && <p className="session-summary-end-note">{w(data.complete ? 'complete' : 'resultPaused')}</p>}
      </header>
      {loading && <p role="status">{w('loading')}</p>}
      {error && <div role="alert" className="writing-result-error"><p>{error}</p><button type="button" onClick={load}>{w('retry')}</button></div>}
      {data && <>
        <div className="session-summary-stats">
          {metrics.map(([label, value, Icon], index) => <div key={label} className="stat-card result-reveal" style={resultRevealStyle(resultMetricDelay(index))} data-result-tone={index}><Icon className="stat-icon" size={22} aria-hidden="true" /><div className="stat-value">{value}</div><div className="stat-label">{w(label)}</div></div>)}
        </div>
        <div className="session-summary-time" aria-label={w('resultRecords')}>
          {[['resultTime', clock(data.activeMs)], ['resultGraded', data.graded], ['resultSkipped', data.skipped]].map(([label, value], index) =>
            <div key={label} className="record-card result-reveal" style={resultRevealStyle(resultRecordDelay(index))}><div className="record-values"><span>{w(label)}</span><strong>{value}</strong></div></div>)}
        </div>
      </>}
      <div className="session-summary-actions"><button type="button" className="btn-back btn-result-home" onClick={() => navigate('/level-up', { replace: true, state: { fromWritingPractice: true, writingComplete: Boolean(data?.complete) } })}><ArrowLeft size={18} aria-hidden="true" />{w('resultBack')}</button></div>
    </section>
  </main>;
}
